import { useRef, useEffect, useCallback } from 'react';
import { useAnimate } from './useAnimate';

/* eslint no-param-reassign: 0 */

export const parseStyleValue = (styleString) => {
  if (styleString === 'auto') {
    return 0;
  }
  return parseInt(styleString, 10);
};

export const useDragHandler = (containerRef, linksRef) => {
  const pointerStartRef = useRef();
  const overscrollRef = useRef({
    left: false,
    right: false
  });
  const velocityRef = useRef(0);
  const isDragRef = useRef(false);
  const lastDragRef = useRef(null);

  const updateOverscroll = (newOffsetLeft, linksWidth) => {
    overscrollRef.current.left = newOffsetLeft > 0;
    overscrollRef.current.right = newOffsetLeft < (containerRef.current.clientWidth - linksWidth);
  };

  const drag = useCallback((event) => {
    if (containerRef.current.clientWidth >= linksRef.current.clientWidth) {
      return;
    }

    isDragRef.current = true;
    if (!linksRef.current.hasPointerCapture(event.pointerId)) {
      linksRef.current.setPointerCapture(event.pointerId);
    }
    let style = window.getComputedStyle(linksRef.current);
    let leftValue = parseStyleValue(style.left);
    let linksWidth = parseStyleValue(style.width);
    const newOffsetLeft = leftValue + event.movementX;
    linksRef.current.style.left = `${newOffsetLeft}px`;

    updateOverscroll(newOffsetLeft, linksWidth);

    if (!lastDragRef.current) {
      lastDragRef.current = Date.now();
    } else {
      const now = Date.now();
      const elapsed = now - lastDragRef.current;
      lastDragRef.current = now;

      if (elapsed === 0) {
        return;
      }
      // Math from -> https://mortoray.com/measuring-finger-mouse-velocity-at-release-time/
      const currentVelocity = event.movementX / elapsed;
      const alpha = 1 - (Math.E ** ((-1 * elapsed) / 0.1));
      const movingAverage = alpha * currentVelocity + (1 - alpha) * velocityRef.current;
      velocityRef.current = movingAverage;
    }
  }, [linksRef, containerRef]);

  const interruptClicksIfDragging = useCallback((event) => {
    const {
      clientX: mouseX,
      clientY: mouseY
    } = event;
    const {
      clientX: startX,
      clientY: startY
    } = pointerStartRef.current;

    if (Math.abs(mouseX - startX) > 5 || Math.abs(mouseY - startY) > 5) {
      event.stopPropagation();
      event.preventDefault();
    }

    pointerStartRef.current = null;
  }, [pointerStartRef]);

  const dragEnd = useCallback((event) => {
    linksRef.current.releasePointerCapture(event.pointerId);
    isDragRef.current = false;
    document.removeEventListener('pointermove', drag);
    document.removeEventListener('pointerup', dragEnd);
  }, [linksRef, isDragRef, drag]);

  const dragStart = useCallback((event) => {
    pointerStartRef.current = {
      clientX: event.clientX,
      clientY: event.clientY,
      time: Date.now()
    };

    document.addEventListener('pointermove', drag);
    document.addEventListener('pointerup', dragEnd);
  }, [drag, dragEnd]);

  const handleWheel = useCallback((event) => {
    if (event.deltaX === 0 || Math.abs(event.deltaX) < Math.abs(event.deltaY)) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();

    let style = window.getComputedStyle(linksRef.current);
    let leftValue = parseStyleValue(style.left);
    let linksWidth = parseStyleValue(style.width);
    const rightBoundary = containerRef.current.clientWidth - linksWidth;

    const newOffsetLeft = Math.max(Math.min(leftValue - event.deltaX, 0), rightBoundary);
    linksRef.current.style.left = `${newOffsetLeft}px`;
  }, [linksRef, containerRef]);

  useEffect(() => {
    if (linksRef.current) {
      linksRef.current.addEventListener('click', interruptClicksIfDragging);
      linksRef.current.addEventListener('pointerdown', dragStart);
      linksRef.current.addEventListener('wheel', handleWheel, { passive: false });
    }

    return () => {
      if (linksRef.current) {
        linksRef.current.removeEventListener('pointerdown', dragStart);
        linksRef.current.removeEventListener('click', interruptClicksIfDragging);
        linksRef.current.removeEventListener('wheel', handleWheel);
      }

      document.removeEventListener('pointermove', drag);
      document.removeEventListener('pointerup', dragEnd);
    };
  }, []);

  const handleResize = useCallback(() => {
    let style = window.getComputedStyle(linksRef.current);
    let leftValue = parseStyleValue(style.left);
    let linksWidth = parseStyleValue(style.width);

    if (containerRef.current.clientWidth >= linksRef.current.clientWidth) {
      linksRef.current.style.left = '0px';
    } else {
      updateOverscroll(leftValue, linksWidth);
    }

  }, [linksRef]);

  useEffect(() => {
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Gets the velocity, based on a positive number for the overscroll amount
  // Direction needs to be accounted for
  const getOverscrollVelocity = (overScrollAmount) => {
    const width = containerRef.current.clientWidth;
    const maxVelocity = 4;
    const xValue = Math.min(overScrollAmount, width);

    // Quadratic easing, basic function is:
    // y = 1 - x^2, but we scale by max velocity and the width of the container
    // We approach maxVelocity as we approach the edge of the container, and 0 as we return
    return (maxVelocity * (1 - ((xValue - width) / width) ** 2));
  };

  useAnimate((elapsed) => {
    // Return if:
    if (
      // dragging
      isDragRef.current
      || (
        // OR velocity is too low
        Math.abs(velocityRef.current) < 0.05
        // AND no overscroll
        && !(overscrollRef.current.left || overscrollRef.current.right)
      )
      || containerRef.current.clientWidth >= linksRef.current.clientWidth
    ) {
      return;
    }

    const { left, right } = overscrollRef.current;
    const style = window.getComputedStyle(linksRef.current);
    const leftValue = parseStyleValue(style.left);
    const linksWidth = parseStyleValue(style.width);
    const rightBoundary = containerRef.current.clientWidth - linksWidth;

    if (left) {
      velocityRef.current = -1 * getOverscrollVelocity(leftValue);
    } else if (right) {
      const overScrollAmount = -1 * (leftValue - rightBoundary);
      velocityRef.current = getOverscrollVelocity(overScrollAmount);
    }

    // Making sure that the delta is always at least 1 pixel
    const delta = Math[velocityRef.current < 0 ? 'floor' : 'ceil'](velocityRef.current * elapsed);
    let newOffsetLeft = leftValue + delta;

    linksRef.current.style.left = `${newOffsetLeft}px`;

    const friction = 0.95;
    velocityRef.current *= friction;

    updateOverscroll(newOffsetLeft, linksWidth);
  });
};